import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';

import { ROUTES } from '@/config/routes';

import { SetupGate } from './components/SetupGate';

const LinkedInDashboardPage = lazy(() =>
  import('./routes/LinkedInDashboardPage').then((m) => ({ default: m.LinkedInDashboardPage })),
);
const LinkedInSettingsPage = lazy(() =>
  import('./routes/LinkedInSettingsPage').then((m) => ({ default: m.LinkedInSettingsPage })),
);
const LinkedInConnectPage = lazy(() =>
  import('./routes/LinkedInConnectPage').then((m) => ({ default: m.LinkedInConnectPage })),
);

const LinkedInWelcomePage = lazy(() =>
  import('./routes/LinkedInWelcomePage').then((m) => ({ default: m.LinkedInWelcomePage })),
);
const LinkedInCompanyDetailsPage = lazy(() =>
  import('./routes/LinkedInCompanyDetailsPage').then((m) => ({
    default: m.LinkedInCompanyDetailsPage,
  })),
);
const LinkedInLaunchPage = lazy(() =>
  import('./routes/LinkedInLaunchPage').then((m) => ({ default: m.LinkedInLaunchPage })),
);
const LinkedInReferencesPage = lazy(() =>
  import('./routes/LinkedInReferencesPage').then((m) => ({ default: m.LinkedInReferencesPage })),
);
const LinkedInProspectsPage = lazy(() =>
  import('./routes/LinkedInProspectsPage').then((m) => ({ default: m.LinkedInProspectsPage })),
);
const LinkedInReviewPage = lazy(() =>
  import('./routes/LinkedInReviewPage').then((m) => ({ default: m.LinkedInReviewPage })),
);
const LinkedInStrategyPage = lazy(() =>
  import('./routes/LinkedInStrategyPage').then((m) => ({ default: m.LinkedInStrategyPage })),
);
const LinkedInConversationsPage = lazy(() =>
  import('./routes/LinkedInConversationsPage').then((m) => ({
    default: m.LinkedInConversationsPage,
  })),
);
const LinkedInContentPage = lazy(() =>
  import('./routes/LinkedInContentPage').then((m) => ({ default: m.LinkedInContentPage })),
);
const LinkedInApprovalsPage = lazy(() =>
  import('./routes/LinkedInApprovalsPage').then((m) => ({ default: m.LinkedInApprovalsPage })),
);
const LinkedInActivityPage = lazy(() =>
  import('./routes/LinkedInActivityPage').then((m) => ({ default: m.LinkedInActivityPage })),
);
const LinkedInAnalyticsPage = lazy(() =>
  import('./routes/LinkedInAnalyticsPage').then((m) => ({ default: m.LinkedInAnalyticsPage })),
);
const LinkedInProspectDetailPage = lazy(() =>
  import('./routes/LinkedInProspectDetailPage').then((m) => ({
    default: m.LinkedInProspectDetailPage,
  })),
);
const LinkedInConfigurationPage = lazy(() =>
  import('./routes/LinkedInConfigurationPage').then((m) => ({
    default: m.LinkedInConfigurationPage,
  })),
);

/**
 * The LinkedIn Agent screens that are built.
 *
 * <p>The setup journey's **welcome** (which hosts the configuration wizard, steps one to four), the
 * standalone **configuration** and **company details** screens, and **Connect LinkedIn**, the sixth
 * step: the ones the backend can serve. Launch, review and the dashboard open a placeholder until
 * their endpoints and screens are ported.
 *
 * <p>Listed inside the `<ProductRoute />` group in `app/router`, ahead of `placeholderRoutes`, for
 * the reasons `features/emailWarmup/index.tsx` gives: the guard applies by placement, and the
 * placeholder would otherwise register a second element for the same path. The path is in
 * `app/navigation/screens.ts` with `linkedin_ai_agent` and in the placeholder's `BUILT_ROUTES`.
 */
export const linkedinAgentRoutes: RouteObject[] = [
  {
    path: ROUTES.linkedinAgent,
    element: (
      <SetupGate>
        <LinkedInDashboardPage />
      </SetupGate>
    ),
  },
  { path: ROUTES.linkedinSettings, element: <LinkedInSettingsPage /> },
  { path: ROUTES.linkedinWelcome, element: <LinkedInWelcomePage /> },
  { path: ROUTES.linkedinCompanyDetails, element: <LinkedInCompanyDetailsPage /> },
  { path: ROUTES.linkedinConfiguration, element: <LinkedInConfigurationPage /> },
  { path: ROUTES.linkedinLaunch, element: <LinkedInLaunchPage /> },
  { path: ROUTES.linkedinReferences, element: <LinkedInReferencesPage /> },
  { path: ROUTES.linkedinConnect, element: <LinkedInConnectPage /> },
  { path: ROUTES.linkedinProspects, element: <LinkedInProspectsPage /> },
  { path: ROUTES.linkedinProspectDetail, element: <LinkedInProspectDetailPage /> },
  { path: ROUTES.linkedinStrategy, element: <LinkedInStrategyPage /> },
  { path: ROUTES.linkedinConversations, element: <LinkedInConversationsPage /> },
  { path: ROUTES.linkedinContent, element: <LinkedInContentPage /> },
  { path: ROUTES.linkedinApprovals, element: <LinkedInApprovalsPage /> },
  { path: ROUTES.linkedinActivity, element: <LinkedInActivityPage /> },
  { path: ROUTES.linkedinAnalytics, element: <LinkedInAnalyticsPage /> },
  { path: ROUTES.linkedinReview, element: <LinkedInReviewPage /> },
];

export { useWorkspaceAnalytics as useLinkedInWorkspaceAnalytics } from './hooks/useActivity';
export { useDashboardSummary as useLinkedInDashboardSummary } from './hooks/useDashboardSummary';
export type { LinkedInDashboardSummary } from './hooks/useDashboardSummary';
